import React from "react";

import moment from "moment";

import type { PublicTrip } from "@busbud/core-types/dist/lib/poll-service/api";
import { Tag } from "@busbud/horizon";

import {
  formatDate,
  getDayDifference,
  isToday,
  isTomorrow
} from "@app/helpers/date-helper";
import { useAppContext } from "@app/helpers/hooks";
import { getOutboundDepartureInternationalTime } from "@app/helpers/udm/get-departure-time";
import type { TranslatorService } from "@app/services/translator";
import type { NapiPurchaseCancellationState } from "@app/types/napi-v2-cancellation-types";

const getChipLabel = (
  cancellation: NapiPurchaseCancellationState | null,
  trip: PublicTrip,
  archived: boolean,
  translator: TranslatorService
) => {
  if (archived) {
    if (cancellation?.created_at) {
      return translator.t("!accounts.my-tickets.cancelled");
    }
    return null;
  }

  const { timezone, timestamp } = getOutboundDepartureInternationalTime(trip)!;
  const date = formatDate(moment(timestamp));

  return isToday(timezone, date)
    ? translator.t("!accounts.my-tickets.today")
    : isTomorrow(timezone, date)
      ? translator.t("!route.price_calendar.tomorrow")
      : translator.t("!accounts.trips.upcoming.days-before-departure", {
          number_of_days: getDayDifference(
            new Date().toISOString(),
            new Date(timestamp).toISOString()
          )
        });
};

export interface Props {
  archived?: boolean;
  cancellation: NapiPurchaseCancellationState | null;
  trip: PublicTrip;
}

export const TripItemStatus: React.FC<Props> = ({
  archived = true,
  cancellation,
  trip
}) => {
  const { translator } = useAppContext();
  const chip_label = getChipLabel(cancellation, trip, archived, translator);

  if (!chip_label) {
    return null;
  }

  return (
    <Tag variant="subtle" colorScheme={archived ? "neutral" : "notice"}>
      {chip_label}
    </Tag>
  );
};
